import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useServerError} from "../../../../hooks/useServerError";
import apiAxios from "../../../../app/api/apiAxios";
import ResultCode from "../../../../data/enums/ResultCode";
import getServerErrorResourceName from "../../../../data/apiErrors/apiErrors";
import {toast} from "react-toastify";
import FormLayout from "../../../ui/layouts/FormLayout/FormLayout";
import Button, {ButtonType} from "../../../ui/components/Button/Button";
import ServiceWorkForm from "./ServiceWorkForm";
import {useSelector} from "react-redux";
import {selectCurrentUser} from "../../../../features/authentication/authenticationSelectors";
import PermissionType from "../../../../data/enums/PermissionType";

const initialState = {
    id: 0,
    createdOn: null,
    name: null,
    crmId: null,
    cost: null,
    unit: null,
    unitName: '',
    description: null,
    service360Id: null,
    service360Name: null,
    isBlocked: false,
    modifiedOn: null,
    targets: []
};

const ServiceWork = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const user = useSelector(selectCurrentUser);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(initialState);
    const [serverError, setServerError] = useServerError(null);

    const canEdit = user?.permission?.services === PermissionType.Editing;

    useEffect(() => {
        (async () => await load(id))();
    }, [id]);

    const load = async (serviceWorkId) => {
        if (!serviceWorkId || serviceWorkId <= 0) {
            navigate(-1);
            return;
        }

        setServerError(null);
        setPage(initialState);
        setIsLoading(true);

        const url = `${process.env.REACT_APP_BE_DOMAIN}/api/servicework/getservicework?id=${serviceWorkId}`;

        try {
            const response = await apiAxios.get(url);

            if (response?.status === 200 && response.data) {
                if (!response.data.resultCode || response.data.resultCode === ResultCode.Success) {
                    setPage({
                        ...initialState,
                        ...response.data,
                        targets: response.data.targets ?? []
                    });
                } else {
                    setServerError(t(getServerErrorResourceName({
                        model: 'serviceWork',
                        resultCode: response.data.resultCode
                    })));
                }
            }

            setIsLoading(false);
        } catch (e) {
            if (e?.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'serviceWork',
                    resultCode: ResultCode.ServerError
                }))} Message: (${e.message})`);
            }
            setIsLoading(false);
        }
    };

    const save = async (values, actions) => {
        try {
            setServerError(null);
            setIsLoading(true);
            actions.setSubmitting(true);

            const response = await apiAxios.post(`${process.env.REACT_APP_BE_DOMAIN}/api/servicework/save`, {
                id: page.id,
                name: page.name,
                crmId: page.crmId,
                cost: page.cost,
                unit: page.unit,
                unitName: page.unitName,
                description: page.description,
                service360Id: page.service360Id,
                isBlocked: page.isBlocked,
                modifiedOn: page.modifiedOn,
                targets: values.targets
            });

            if (response?.status === 200 && response.data) {
                if (response.data.resultCode === ResultCode.Success) {
                    toast.success(t("serviceWork.form.saved-label"));

                    if (response.data.description) {
                        await load(+response.data.description);
                    }
                } else {
                    setServerError(t(getServerErrorResourceName({
                        model: 'serviceWork',
                        resultCode: response.data.resultCode,
                        description: response.data.description
                    })));
                }
            }

            setIsLoading(false);
            actions.setSubmitting(false);
        } catch (err) {
            if (err?.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'serviceWork',
                    resultCode: ResultCode.ServerError
                }))} Message: (${err.message})`);
            }
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };

    return (
        <FormLayout header={<div className='default-form-layout-header'>{t("serviceWork.form.form-label")}</div>}
                    footer={(
                        <div className='default-form-layout-footer'>
                            <Button buttonType={ButtonType.Back}
                                    onClick={() => navigate(-1)}
                                    disabled={isLoading}>
                                {t("btn.back")}
                            </Button>
                            {canEdit
                                ? <Button buttonType={ButtonType.Default}
                                          form='service-work-form'
                                          type='submit'
                                          disabled={isLoading}>
                                    {t("btn.save")}
                                </Button>
                                : null
                            }
                        </div>
                    )}
                    footerPosition='top'
                    isLoading={isLoading}>
            <div className='default-form-layout-container'>
                {serverError &&
                    <div className='server-error-container'>
                        <div>
                            {serverError}
                        </div>
                    </div>
                }
                <ServiceWorkForm {...page} disabled={!canEdit} onSubmit={save}/>
            </div>
        </FormLayout>
    );
};

export default ServiceWork;

